import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Warning, CheckCircle, ArrowsClockwise, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtMoney = (n) => `$ ${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Modal para sincronizar precios de la lista marcada como "TN" hacia Tienda Nube.
 * Flujo: preview (dry-run) → deselección opcional → confirmar y aplicar.
 * Reglas de negocio implementadas en backend:
 * - 1 producto local → clonar mismo precio a todas las variantes TN.
 * - Se saltean variaciones < max($1, 0.5%) (ruido).
 * - Se saltean productos sin precio en la lista TN.
 */
export default function SyncPreciosTNDialog({ open, onOpenChange }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [deseleccionados, setDeseleccionados] = useState(new Set());
  const [tab, setTab] = useState("cambios"); // cambios | sin_precio | sin_match

  useEffect(() => {
    if (!open) return;
    setPlan(null);
    setDeseleccionados(new Set());
    setTab("cambios");
    setLoading(true);
    api.get("/tiendanube/sync-precios/preview")
      .then(({ data }) => setPlan(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo cargar el preview"))
      .finally(() => setLoading(false));
  }, [open]);

  const cambios = plan?.cambios || [];
  const sinPrecio = plan?.sin_precio || [];
  const sinMatch = plan?.sin_match || [];

  const seleccionados = useMemo(
    () => cambios.filter((c) => !deseleccionados.has(String(c.tn_variant_id))),
    [cambios, deseleccionados]
  );

  const toggleOne = (vid) => {
    setDeseleccionados((s) => {
      const n = new Set(s);
      const k = String(vid);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  };

  const aplicar = async () => {
    if (seleccionados.length === 0) {
      return toast.error("No hay cambios seleccionados para aplicar");
    }
    if (!window.confirm(
      `¿Aplicar ${seleccionados.length} cambio${seleccionados.length === 1 ? "" : "s"} de precio en Tienda Nube?\n\n` +
      "Esta acción es inmediata y los precios se actualizan en la tienda."
    )) return;
    setApplying(true);
    try {
      const ids = seleccionados.map((c) => c.tn_variant_id);
      const { data } = await api.post("/tiendanube/sync-precios/apply", { tn_variant_ids: ids });
      toast.success(`Precios sincronizados · ${data.aplicados} OK · ${data.fallidos} fallos`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aplicar");
    } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="sync-precios-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Storefront size={18} weight="bold" className="text-sky-700" />
            Sincronizar precios con Tienda Nube
          </DialogTitle>
          {plan && (
            <div className="text-[12px] text-neutral-600 font-mono">
              Lista de origen: <span className="font-bold text-sky-800">{plan.lista_tn?.nombre}</span>
              {" · "}Tolerancia: max($1, 0,5%) — cambios menores se saltean
            </div>
          )}
        </DialogHeader>

        {loading && <div className="p-12 text-center text-neutral-500">Analizando productos en Tienda Nube…</div>}

        {!loading && plan && (
          <>
            <div className="flex items-center gap-2 border-b border-neutral-200 pb-2">
              <button
                onClick={() => setTab("cambios")}
                className={`px-3 py-1 text-[12px] font-mono uppercase rounded-sm ${
                  tab === "cambios" ? "bg-sky-600 text-white" : "bg-neutral-100 hover:bg-neutral-200"
                }`}
                data-testid="sync-tab-cambios"
              >Cambios ({cambios.length})</button>
              <button
                onClick={() => setTab("sin_precio")}
                className={`px-3 py-1 text-[12px] font-mono uppercase rounded-sm ${
                  tab === "sin_precio" ? "bg-amber-600 text-white" : "bg-neutral-100 hover:bg-neutral-200"
                }`}
                data-testid="sync-tab-sin-precio"
              >Sin precio TN ({sinPrecio.length})</button>
              <button
                onClick={() => setTab("sin_match")}
                className={`px-3 py-1 text-[12px] font-mono uppercase rounded-sm ${
                  tab === "sin_match" ? "bg-neutral-700 text-white" : "bg-neutral-100 hover:bg-neutral-200"
                }`}
                data-testid="sync-tab-sin-match"
              >Sin match ({sinMatch.length})</button>
              <span className="ml-auto text-[11px] font-mono text-neutral-500">
                Sin cambio (dentro tolerancia): {plan.sin_cambio}
              </span>
            </div>

            <div className="flex-1 overflow-y-auto border border-neutral-200 rounded-sm">
              {tab === "cambios" && (
                cambios.length === 0 ? (
                  <div className="p-12 text-center text-neutral-500 flex flex-col items-center gap-2">
                    <CheckCircle size={32} weight="fill" className="text-emerald-500" />
                    No hay cambios de precio para aplicar. Todos los precios en TN ya coinciden con la lista.
                  </div>
                ) : (
                  <table className="w-full text-[12px]">
                    <thead className="bg-neutral-50 sticky top-0 text-[10px] uppercase font-mono text-neutral-600">
                      <tr>
                        <th className="px-2 py-2 w-8 text-center">
                          <input
                            type="checkbox"
                            checked={seleccionados.length === cambios.length}
                            onChange={() => {
                              setDeseleccionados(seleccionados.length === cambios.length
                                ? new Set(cambios.map((c) => String(c.tn_variant_id)))
                                : new Set());
                            }}
                            data-testid="sync-toggle-all"
                          />
                        </th>
                        <th className="px-3 py-2 text-left">SKU</th>
                        <th className="px-3 py-2 text-left">Producto TN · Local</th>
                        <th className="px-3 py-2 text-right w-28">Precio actual</th>
                        <th className="px-3 py-2 text-right w-28">Precio nuevo</th>
                        <th className="px-3 py-2 text-right w-24">Δ $</th>
                        <th className="px-3 py-2 text-right w-20">Δ %</th>
                        <th className="px-3 py-2 text-left w-24">Modo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cambios.map((c, i) => {
                        const sel = !deseleccionados.has(String(c.tn_variant_id));
                        const up = c.diferencia_abs > 0;
                        return (
                          <tr key={i}
                              className={`border-t border-neutral-100 ${sel ? "" : "opacity-40 bg-neutral-50"}`}
                              data-testid={`sync-row-${i}`}>
                            <td className="px-2 py-1.5 text-center">
                              <input type="checkbox" checked={sel}
                                     onChange={() => toggleOne(c.tn_variant_id)}
                                     data-testid={`sync-check-${i}`} />
                            </td>
                            <td className="px-3 py-1.5 font-mono font-bold">{c.sku}</td>
                            <td className="px-3 py-1.5">
                              <div className="font-medium truncate max-w-[280px]" title={c.nombre_tn}>{c.nombre_tn}</div>
                              <div className="text-[10px] text-neutral-500 font-mono">{c.nombre_local}</div>
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(c.precio_tn_actual)}</td>
                            <td className="px-3 py-1.5 text-right font-mono font-bold">{fmtMoney(c.precio_nuevo)}</td>
                            <td className={`px-3 py-1.5 text-right font-mono font-bold ${up ? "text-emerald-700" : "text-rose-700"}`}>
                              {up ? "+" : ""}{fmtMoney(c.diferencia_abs)}
                            </td>
                            <td className={`px-3 py-1.5 text-right font-mono ${up ? "text-emerald-700" : "text-rose-700"}`}>
                              {c.diferencia_pct != null ? `${c.diferencia_pct > 0 ? "+" : ""}${c.diferencia_pct}%` : "—"}
                            </td>
                            <td className="px-3 py-1.5 text-[10px] font-mono text-neutral-500 uppercase">
                              {c.modo === "clon_variantes" ? "clon" : "1:1"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              )}

              {tab === "sin_precio" && (
                <table className="w-full text-[12px]">
                  <thead className="bg-amber-50 text-[10px] uppercase font-mono text-amber-900">
                    <tr>
                      <th className="px-3 py-2 text-left">Código</th>
                      <th className="px-3 py-2 text-left">Producto local</th>
                      <th className="px-3 py-2 text-left">En TN</th>
                      <th className="px-3 py-2 text-center">Variantes afectadas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sinPrecio.length === 0 ? (
                      <tr><td colSpan={4} className="px-3 py-8 text-center text-neutral-400">Todos los productos tienen precio en la lista TN.</td></tr>
                    ) : sinPrecio.map((r, i) => (
                      <tr key={i} className="border-t border-amber-100">
                        <td className="px-3 py-1.5 font-mono">{r.codigo}</td>
                        <td className="px-3 py-1.5">{r.nombre_local}</td>
                        <td className="px-3 py-1.5 text-neutral-600">{r.nombre_tn}</td>
                        <td className="px-3 py-1.5 text-center font-mono">{r.variantes_afectadas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {tab === "sin_match" && (
                <table className="w-full text-[12px]">
                  <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600">
                    <tr>
                      <th className="px-3 py-2 text-left">SKU (TN)</th>
                      <th className="px-3 py-2 text-left">Producto TN</th>
                      <th className="px-3 py-2 text-right">Precio actual TN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sinMatch.length === 0 ? (
                      <tr><td colSpan={3} className="px-3 py-8 text-center text-neutral-400">Todas las variantes matchean con el catálogo local.</td></tr>
                    ) : sinMatch.map((r, i) => (
                      <tr key={i} className="border-t border-neutral-100">
                        <td className="px-3 py-1.5 font-mono">{r.sku}</td>
                        <td className="px-3 py-1.5">{r.nombre_tn}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(r.precio_tn_actual)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {tab === "cambios" && (sinPrecio.length > 0 || sinMatch.length > 0) && (
              <div className="text-[11px] text-amber-800 flex items-center gap-1.5">
                <Warning size={12} weight="fill" />
                {sinPrecio.length > 0 && <span>{sinPrecio.length} productos sin precio en la lista TN (revisá pestaña)</span>}
                {sinPrecio.length > 0 && sinMatch.length > 0 && " · "}
                {sinMatch.length > 0 && <span>{sinMatch.length} variantes sin match por SKU</span>}
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
            Cancelar
          </Button>
          <Button
            onClick={aplicar}
            disabled={loading || applying || seleccionados.length === 0}
            className="bg-sky-700 hover:bg-sky-800 text-white gap-1"
            data-testid="sync-precios-aplicar"
          >
            {applying ? <ArrowsClockwise size={14} weight="bold" className="animate-spin" /> : <Storefront size={14} weight="bold" />}
            Aplicar {seleccionados.length} cambio{seleccionados.length === 1 ? "" : "s"} en Tienda Nube
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
